import React from 'react';
import { IMatrixMessage, ITextMessageContent } from '../../client/message/types';
import { IEmergencyContactInviteMessage } from '../../client/message/messageUtils';
import { TextMessage } from './text/TextMessage';
import { LocationMessage } from './location/LocationMessage';
import { messageUtils } from '../../client/message/messageUtils';
import { InviteMessage } from './invite/InviteMessage';
import { RoomNameMessage } from './room/RoomNameMessage';
import { RoomHistoryVisibilityMessage } from './room/RoomHistoryVisibilityMessage';
import { RoomJoinRuleMessage } from './room/RoomJoinRuleMessage';
import { RoomPowerLevelsMessage } from './room/RoomPowerLevelsMessage';
import { RoomGuestAccessMessage } from './room/RoomGuestAccessMessage';
import { RoomTopicMessage } from './room/RoomTopicMessage';
import { RoomCreateMessage } from './room/RoomCreateMessage';
import { RoomAvatarMessage } from './room/RoomAvatarMessage';
import { RoomCanonicalAliasMessage } from './room/RoomCanonicalAliasMessage';
import { RoomTombstoneMessage } from './room/RoomTombstoneMessage';
import { RoomEncryptionMessage } from './room/RoomEncryptionMessage';
import { RoomServerAclMessage } from './room/RoomServerAclMessage';
import { EmergencyContactInviteMessage } from './emc_invite/EmergencyContactInviteMessage';
import { Log } from '@fanfanlo';

const fileLogger = new Log(false, 'MessageRenderer_f')
// fileLogger.childrenPaused = true;
// fileLogger.pause = true;
interface MessageRendererProps {
  message: IMatrixMessage;
  className?: string;
}

export const MessageRenderer = ({ 
  message, 
  className = '' 
}: MessageRendererProps) => {
  const logger = fileLogger.sub(false, 'MessageRenderer_ui')
  logger.childrenPaused = true;
  logger.pause = true;
  const commonProps = {
    message,
    className
  };

  // 添加更详细的日志记录
  logger.log('[MessageRenderer] 开始渲染消息:', {
    event_id: message.event_id,
    type: message.type,
    msgtype: message.content.msgtype,
    hasCustom: !!message.content.custom,
    customKeys: message.content.custom ? Object.keys(message.content.custom) : []
  });

  // 检查是否是紧急联系人邀请消息
  const isEmergencyInvite = messageUtils.isEmergencyContactInvite(message);
  fileLogger.log('[MessageRenderer] 检查紧急联系人邀请消息:', {
    eventId: message.event_id,
    isEmergencyInvite,
    msgtype: message.content.msgtype,
    hasCustom: !!message.content.custom,
    customKeys: message.content.custom ? Object.keys(message.content.custom) : []
  });

  if (isEmergencyInvite) {
    // 获取邀请数据，支持新旧格式
    fileLogger.log('[MessageRenderer] 开始处理紧急联系人邀请消息', {
      eventId: message.event_id,
      content: message.content,
      customContent: message.content.custom?.['app.emergency_contact.invite']
    });
    
    const inviteData = messageUtils.getEmergencyContactInviteData(message);
    
    if (!inviteData) {
      const errorMsg = '[MessageRenderer] 无效的紧急联系人邀请消息: 缺少邀请数据';
      fileLogger.error(errorMsg, { message });
      // 创建一个简单的文本消息作为回退
      const fallbackMessage = {
        ...message,
        content: {
          msgtype: 'm.text' as const,
          body: '无效的紧急联系人邀请消息',
          // 确保包含 ITextMessageContent 所需的其他字段
          format: 'org.matrix.custom.html',
          formatted_body: '无效的紧急联系人邀请消息',
          // 保留原始消息的关联信息
          'm.relates_to': message.content['m.relates_to']
        }
      } as const;
      
      // 使用类型断言确保类型正确
      return <TextMessage message={fallbackMessage as unknown as IMatrixMessage} className={commonProps.className} />;
    }

    logger.log('[MessageRenderer] 检测到紧急联系人邀请消息:', {
      event_id: message.event_id,
      msgtype: message.content.msgtype,
      inviteData,
      hasCustomData: !!message.content.custom
    });

    // 创建标准化的消息对象
    const enhancedMessage: IEmergencyContactInviteMessage = {
      ...message,
      content: {
        ...message.content,
        msgtype: 'm.emergency_contact.invite' as const,
        'com.droidlego.emergency_contact': inviteData,
        // 确保有默认文本
        body: inviteData.message || 
             message.content.body || 
             `${inviteData.inviter.display_name} 邀请你成为紧急联系人`,
        // 保留原始自定义数据以便向后兼容
        custom: {
          ...(message.content.custom || {}),
          'app.emergency_contact.invite': {
            invite_id: inviteData.invite_id,
            inviter_id: inviteData.inviter.user_id,
            inviter_info: {
              display_name: inviteData.inviter.display_name,
              avatar_url: inviteData.inviter.avatar_url
            },
            status: inviteData.status,
            permissions: inviteData.permissions,
            created_ts: inviteData.created_ts,
            expires_ts: inviteData.expires_ts,
            content: inviteData.message
          }
        }
      }
    };

    logger.log('[MessageRenderer] 渲染 EmergencyContactInviteMessage', {
      eventId: message.event_id,
      enhancedMessage: {
        ...enhancedMessage,
        content: {
          ...enhancedMessage.content,
          // 避免日志过大，只记录关键信息
          custom: enhancedMessage.content.custom ? '[...]' : undefined
        }
      }
    });
    
    return (
      <div style={{ border: '2px solid red', padding: '8px', margin: '4px 0' }} data-testid="emergency-invite-container">
        <EmergencyContactInviteMessage 
          key={message.event_id} 
          {...commonProps} 
          message={enhancedMessage} 
        />
      </div>
    );
  }

  // 房间加群规则
  if (message.content && message.content.join_rule) {
    return <RoomJoinRuleMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 房间权限设置
  if (message.content && (
    message.content.events_default !== undefined ||
    message.content.users !== undefined ||
    message.content.ban !== undefined ||
    message.content.kick !== undefined ||
    message.content.redact !== undefined
  )) {
    return <RoomPowerLevelsMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 访客访问权限
  if (message.content && message.content.guest_access) {
    return <RoomGuestAccessMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 房间话题
  if (message.content && message.content.topic) {
    return <RoomTopicMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 房间历史可见性变更
  if (message.content && message.content.history_visibility) {
    return <RoomHistoryVisibilityMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 房间名称变更
  if (message.content && message.content.name) {
    return <RoomNameMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 邀请和接受���请类型
  if (message.content && (message.content.membership === 'invite' || message.content.membership === 'join')) {
    return <InviteMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 房间创建
  if (message.content && message.content.room_version && message.content.creator) {
    return <RoomCreateMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 房间头像
  if (message.content && message.content.avatar_url) {
    return <RoomAvatarMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 房间主别名
  if (message.content && message.content.canonical_alias) {
    return <RoomCanonicalAliasMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 房间废弃/迁移
  if (message.content && message.content.replacement_room) {
    return <RoomTombstoneMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 启用加密
  if (message.content && message.content.algorithm) {
    return <RoomEncryptionMessage key={message.event_id} {...commonProps} message={message} />;
  }

  // 服务器访问控制
  if (message.content && (message.content.allow || message.content.deny)) {
    return <RoomServerAclMessage key={message.event_id} {...commonProps} message={message} />;
  }

  if (messageUtils.isTextMessage(message)) {
    return <TextMessage key={message.event_id} {...commonProps} message={message} />;
  }

  if (messageUtils.isLocationMessage(message)) {
    return <LocationMessage key={message.event_id} {...commonProps} message={message} />;
  }
  
  // 其他消息类型的处理...
  console.log('unknown message=', message);
  console.log('json message=', JSON.stringify(message));
  return <div className={`unknown-message ${className}`}>
    不支持的消息类型: {message.content.msgtype}
  </div>;
};
